import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { Button } from "react-md";
import { dateTimeToStringWithDOW } from "components/Shared/Formatters";
import { getTabbedHistoryTable } from "components/Homeowner/History/DataFormatters";
import { MSpinner } from "components/Shared";
import { addIncident, updateIncident, deleteIncident, closeIncident } from "requests/customerService/index.ts";
import { getCustomerServiceAssignToUsers } from "actions/customerService";
import { CloseDeleteIncidentDialog, AddEditIncidentDialog, closeDeleteModes as MODES } from "components/CustomerService";
import { databaseTypeCodes, statusOptions, errorOptions } from "typeConstants/customerService";
import MDialog from "components/Shared/MDialog.tsx";
import { CUSTOMER_SERVICE_INCIDENT_ADD, CUSTOMER_SERVICE_INCIDENT_UPDATE, CUSTOMER_SERVICE_INCIDENT_CLOSE, CUSTOMER_SERVICE_INCIDENT_DELETE, CUSTOMER_SERVICE_INCIDENT_DETAILS_VIEW } from "typeConstants/rights";
import { userHasRight } from "selectors/rights";
import ViewIncidentDialog from "./ViewIncidentDialog.tsx";

/* eslint-disable react/display-name */

const getViewDetailsIncidentTableButton = (callback) => {
  return rowData => {
    return (
      <div className="cs-table-action-container">
        <i className="md-icon material-icons" onClick={() => callback(rowData)}>visibility</i>
      </div>
    );
  };
};

const getEditIncidentTableButton = (callback) => {
  return rowData => {
    return (
      <div className="cs-table-action-container">
        <i className="md-icon material-icons" onClick={() => callback(rowData)}>edit</i>
      </div>
    );
  };
};

const getDeleteIncidentTableButton = (callback) => {
  return rowData => {
    return (
      <div className="cs-table-action-container">
        <i className="md-icon material-icons" onClick={() => callback(rowData)}>delete_forever</i>
      </div>
    );
  };
};

const getCloseIncidentTableButton = (callback) => {
  return rowData => {
    if (rowData && rowData.status == 2) return null; //Incident is already closed
    return (
      <div className="cs-table-action-container">
        <i className="md-icon material-icons" onClick={() => callback(rowData)}>done_outline</i>
      </div>
    );
  };
};

/* eslint-enable react/display-name */

const CustomerServiceNotes = (props) => {
  const mounted = useRef(true);

  const [viewIncidentDialogVisible, setViewIncidentDialogVisible] = useState(false);
  const [addEditDialogVisible, setAddEditDialogVisible] = useState(false);
  const [deleteCloseDialogVisible, setCloseDeleteDialogVisible] = useState(false);
  const [unsavedWorkDialogVisible, setUnsavedWorkDialogVisible] = useState(false);
  const [currentMode, setCurrentMode] = useState("");
  const [currentIncident, setCurrentIncident] = useState({ attachments: [], notes: [] }); // type is 'ClientSideIncident' from ClientSideIncident.ts -- right now if this isn't initialized it sets off a cascade of errors
  const [isSubmittingIncident, setIsSubmittingIncident] = useState(false);

  useEffect(() => {
    if ((props.canAdd || props.canUpdate) && props.assignToUsers && !props.assignToUsers.length && !props.isFetchingAssignToUsers) props.getCustomerServiceAssignToUsers();
    if (props.tableData) handleData();
    return (() => { mounted.current = false; });
  }, []);

  const hideCloseDeleteDialog = () => {
    setCloseDeleteDialogVisible(false);
  };
  
  const hideAddEditDialog = () => {
    setAddEditDialogVisible(false);
    if (currentIncident.edited) 
      setUnsavedWorkDialogVisible(true);
    else
      props.refreshCallback();
  };

  const handleData = () => {
    props.tableData.forEach(function (note) {
      note.refreshCallback = props.refreshCallback;
    });
  };

  const getColumnDefinitions = () => {
    let columnDefs = [...props.columnDefinitions];
    if (props.canDelete && !props.viewOnly) columnDefs.push({ name: "Delete", dataSource: getDeleteIncidentTableButton(showDeleteDialog) });
    if (props.canClose && !props.viewOnly) columnDefs.unshift({ name: "Resolve", dataSource: getCloseIncidentTableButton(showCloseDialog) });
    if (props.canUpdate && !props.viewOnly) columnDefs.unshift({ name: "Edit", dataSource: getEditIncidentTableButton(showEditDialog) });
    if (props.canViewDetails) columnDefs.unshift({ name: "View", dataSource: getViewDetailsIncidentTableButton(showIncidentDetails) });
    return columnDefs;
  };

  const setIncidentFromRowData = rowData => {
    setCurrentIncident({
      id: rowData.id,
      controlIdNumber: rowData.controlIDNumber,
      assignedDepartment: rowData.assignedDepartment ? rowData.assignedDepartment.code: "",
      assignedInternetUserId: rowData.assignedInternetUserId ? rowData.assignedInternetUserId.toString() : "",
      status: rowData.statusName.toUpperCase(),
      errorOption: rowData.errorOption ? getErrorOptionForCode(rowData.errorOption.code) : errorOptions[0].value,
      databaseTypeCode: databaseTypeCodes.homeowner(),
      personTypeCode: rowData.personType ? rowData.personType.code : "",
      reasonTypeCode: rowData.callReason ? rowData.callReason.code : "",
      note: rowData.details,
      dateCreated: dateTimeToStringWithDOW(rowData.dateCreated),
      edited: false,
      notes: rowData.notes,
      attachments: rowData.attachments,
      replyToName: rowData.replyToName,
      replyToEmail: rowData.replyToEmail,
      replyToPhone: rowData.replyToPhone,
    });
  };

  const showIncidentDetails = (rowData) => {
    setIncidentFromRowData(rowData);
    setViewIncidentDialogVisible(true);
  };

  const showDeleteDialog = rowData => {
    setIncidentFromRowData(rowData);
    setCurrentMode(MODES.DELETE);
    setCloseDeleteDialogVisible(true);
  };

  const showCloseDialog = rowData => {
    setIncidentFromRowData(rowData);
    setCurrentMode(MODES.CLOSE);
    setCloseDeleteDialogVisible(true);
  };

  const showEditDialog = rowData => {
    setIncidentFromRowData(rowData);
    setAddEditDialogVisible(true);
  };

  const getErrorOptionForCode = code => {
    let option = errorOptions.filter(e => e.code === code);
    return option.length > 0 ? option[0].value : errorOptions[0].value; // Default to none if there's no match
  };

  const deleteFromEditDialogOnClick = () => {
    setAddEditDialogVisible(false);
    setCurrentMode(MODES.DELETE);
    setCloseDeleteDialogVisible(true);
  };

  const showAddDialog = () => {
    setCurrentIncident({
      controlIdNumber: props.controlNumber,
      assignedDepartment: null,
      assignedInternetUserId: "",
      status: statusOptions[0].value, // OPEN
      errorOption: errorOptions[0].value, // None
      databaseTypeCode: databaseTypeCodes.homeowner(),
      personTypeCode: "",
      reasonTypeCode: "",
      note: "",
      edited: false,
      notes: [],
      attachments: [],
      replyToName: "",
      replyToEmail: "",
      replyToPhone: "",
    });
    setAddEditDialogVisible(true);
  };

  const onCloseDeleteConfirm = () => {
    switch (currentMode) {
      case MODES.DELETE:
        deleteIncident(currentIncident.id, props.refreshCallback, mounted);
        break;
      case MODES.CLOSE:
        closeIncident(currentIncident.id, props.refreshCallback, mounted);
        break;
    }
    hideCloseDeleteDialog();
  };

  const handleFormChange = (updatedObject) => {
    // TODO: Figure out why this updatedObject sometimes comes back without an id
    if (currentIncident.id && updatedObject.id === undefined) {
      updatedObject.id = currentIncident.id;
    }
    setCurrentIncident({ ...updatedObject, edited: true });
  };

  const onSubmitUpdate = (submission) => { // submission: CustomerServiceIncidentUpdate
    if (!submission.status || !submission.personTypeCode || !submission.reasonTypeCode) return;

    setAddEditDialogVisible(false);
    setIsSubmittingIncident(true);

    updateIncident(submission, 
    () => {
      setIsSubmittingIncident(false);
      props.refreshCallback();
    },
    ()=> {
      setIsSubmittingIncident(false);
    },
     mounted);
  };

  const onSubmitCreate = (submission) => { // submission: CustomerServiceIncidentAdd
    if (!submission.status || !submission.personTypeCode || !submission.reasonTypeCode) return;

    setAddEditDialogVisible(false);
    setIsSubmittingIncident(true);

    addIncident(submission, 
    () => {
      setIsSubmittingIncident(false);
      props.refreshCallback();
    },
    ()=> {
      setIsSubmittingIncident(false);
    },
    mounted);
  };

  const getUnSavedWorkDialogActions = () => {
    let actions = [];
    actions.push({ secondary: false, children: "No, Go Back", onClick: goBackToEditDialog });
    actions.push(<Button primary raised onClick={hideUnsavedWorkDialog} >Yes, Discard Changes</Button>);
    return actions;
  };
  const hideUnsavedWorkDialog = () => {
    setUnsavedWorkDialogVisible(false);
    props.refreshCallback();
  };
  const goBackToEditDialog = () => {
    setUnsavedWorkDialogVisible(false);
    setAddEditDialogVisible(true);
  };

  if (props.isFetchingAssignToUsers) return <MSpinner />;

  return (
    <>
      {props.canAdd && <span className="customer-service-action"><button className="md-btn md-btn--raised md-btn--text md-pointer--hover md-background--primary md-background--primary-hover md-inline-block"
        onClick={showAddDialog}>Add</button></span>}
      <MDialog 
        visible={isSubmittingIncident}
      >
        <MSpinner />
      </MDialog>
      {props.canViewDetails && currentIncident.assignedInternetUserId &&
        <ViewIncidentDialog 
          incident={currentIncident}
          incidentDialogVisible={viewIncidentDialogVisible}
          hideIncidentDialog={() => setViewIncidentDialogVisible(false)}
          controlNumber={props.controlNumber}
        />
      }
      {(props.canAdd || props.canUpdate) && <AddEditIncidentDialog
        incident={currentIncident}
        title={currentIncident.id ? "Edit Incident" : "Add New Incident"}
        incidentDialogVisible={addEditDialogVisible}
        hideIncidentDialog={hideAddEditDialog}
        onSubmitUpdate={onSubmitUpdate}
        onSubmitCreate={onSubmitCreate}
        isFetchingAssignToUsers={props.isFetchingAssignToUsers}
        controlNumber={props.controlNumber}
        assignToUsers={props.assignToUsers}
        onDeleteClicked={deleteFromEditDialogOnClick}
        canClose={props.canClose}
        canDelete={props.canDelete}
        handleFormChange={handleFormChange}
        customerType={props.customerType}
      />}
      {(props.canClose || props.canDelete) && <CloseDeleteIncidentDialog
        onConfirm={onCloseDeleteConfirm}
        mode={currentMode}
        dialogVisible={deleteCloseDialogVisible}
        hideDialog={hideCloseDeleteDialog}
      />}
      <MDialog
        id="cs-unsaved-work-dialog"
        className="cs-unsaved-work-dialog"
        title="Warning"
        visible={unsavedWorkDialogVisible}
        focusOnMount={false}
        contentActions={getUnSavedWorkDialogActions()}
      >
        <>You have unsaved changes to this note. Do you want to close and lose your changes?</>
      </MDialog>
      {getTabbedHistoryTable(getColumnDefinitions(), props.tableData, "CS Notes", "id")}
    </>
  );
};

const mapStateToProps = (state) => {
  return {
    canAdd: userHasRight(CUSTOMER_SERVICE_INCIDENT_ADD)(state),
    canUpdate: userHasRight(CUSTOMER_SERVICE_INCIDENT_UPDATE)(state),
    canClose: userHasRight(CUSTOMER_SERVICE_INCIDENT_CLOSE)(state),
    canDelete: userHasRight(CUSTOMER_SERVICE_INCIDENT_DELETE)(state),
    canViewDetails: userHasRight(CUSTOMER_SERVICE_INCIDENT_DETAILS_VIEW)(state),
    isFetchingAssignToUsers: state.customerService.isFetchingAssignToUsers,
    assignToUsers: state.customerService.assignToUsers,
    customerType: state.caseForms.caseHeader.policyInfo.customerInfo.customerType,
  };
};

const mapDispatchToProps = (dispatch) => ({
  getCustomerServiceAssignToUsers() {
    dispatch(getCustomerServiceAssignToUsers());
  }
});

CustomerServiceNotes.propTypes = {
  controlNumber: PropTypes.number.isRequired,         // Control number of the case
  columnDefinitions: PropTypes.array,                 // Column definitions for the table
  tableData: PropTypes.array,                         // Incidents data to display in the table
  refreshCallback: PropTypes.func,                    // Callback to get new data after add, edit, close, or delete
  viewOnly: PropTypes.bool,                           // For viewing the CS notes, can not add/update/delete/close even if the user has a right for it

  // From state
  canViewDetails: PropTypes.bool,
  canAdd: PropTypes.bool,                             // If true, the user will be able to add a new incident
  canUpdate: PropTypes.bool,                          // If true, the user will be able to edit incidents
  canClose: PropTypes.bool,                           // If true, the user will be able to change the incident status
  canDelete: PropTypes.bool,                          // If true, the user will be shown the delete button
  assignToUsers: PropTypes.array,                     // Options for Assigned To dropdown
  isFetchingAssignToUsers: PropTypes.bool,            // Used to determine if a spinner or the dialog should be shown
  customerType: PropTypes.string.isRequired,          // Passed down to Add/Edit dialog to determine which reasons list to show

  // From dispatch
  getCustomerServiceAssignToUsers: PropTypes.func.isRequired, // Gets the list of customer service users that can be assigned to
};

export default connect(mapStateToProps, mapDispatchToProps)(CustomerServiceNotes);